import { formatDate } from '@progress/kendo-intl';
import { useCallback } from 'react';
import uuid from 'react-uuid';
import { api } from 'src/assets/static/ApiRoutes';
import useAsync from 'src/hooks/useAsync';
import axiosInstance from 'src/utils/axios';
import { removeTime } from 'src/utils/formatTime';
import { isEqualString } from 'src/utils/strings';
import { convertDate } from 'src/utils/transformation';

interface IPostBody {
  glCodeId: number;
  glPeriodId: number;
}

export interface ActualizedAmountTransaction {
  rowId: string;
  expanded: boolean;
  transactionDate: string;
  date: Date;
  actualDate: Date;
  user: string;
  documentNumber: string;
  invoiceNumber: string;
  invoiceId: number;
  poId: number;
  action: string;
  total: number;
  notes: string;
  poSubject: string;
  comments: string;
  balance: number;
  budgetAmt: number;
  forecast: number;
  glCode: string;
  items: [
    {
      lineItem: string;
      itemNum: string;
      description: string;
      amount: number;
    }
  ];
}

const useGetActualizedTransactionApi = () => {
  const call = useCallback(
    (body?: IPostBody) =>
      new Promise<ActualizedAmountTransaction[]>((resolve, reject) => {
        axiosInstance
          .get<ActualizedAmountTransaction[]>(
            api.getActualizedAmount(body?.glCodeId as number, body?.glPeriodId as number)
          )
          .then((data) => {
            const res = data.data.map((item) => {
              const date = convertDate(item.transactionDate);
              const isJournalEntry = isEqualString(item.action, 'journal entry');
              const action =
                isJournalEntry && item.total <= 0
                  ? 'Journal Entry - Credit'
                  : isJournalEntry && item.total > 0
                  ? 'Journal Entry - Debit'
                  : item.action;

              const actionLowerCase = item.action.toLowerCase();
              const isPOAction =
                !actionLowerCase.includes('invoice completed') &&
                !actionLowerCase.includes('credit memo completed');

              return {
                ...item,
                rowId: uuid(),
                expanded: false,
                transactionDate: formatDate(date, 'MM/dd/yyyy h:mm:ss a'),
                date: removeTime(date),
                actualDate: date,
                action: action,
                actionNotes: isPOAction ? item.poSubject : item.notes,
              };
            });

            return resolve(res);
          })
          .catch((error) => {
            reject(error);
          });
      }),
    []
  );

  const { execute, isLoading } = useAsync(call, false);

  return {
    getActualizedTransaction: execute,
    isSpentLoading: isLoading,
  };
};

export default useGetActualizedTransactionApi;
